import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, Download, XCircle, ArrowUpRight } from "lucide-react";
import { http, pdfUrl, TOKEN_KEY } from "@/lib/api";

export default function PaymentReturn({ cancelled }) {
  const [sp] = useSearchParams();
  const sid = sp.get("session_id");
  const urlToken = sp.get("token");
  const [st, setSt] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (cancelled || !sid) return;
    let alive = true;
    const poll = async (n) => {
      try {
        const { data } = await http.get(`/payments/status/${sid}`, { params: { token: urlToken || localStorage.getItem(TOKEN_KEY) || "" } });
        if (!alive) return;
        setSt(data);
        if (data.payment_status === "paid") {
          if (data.report_type === "ai") http.post(`/results/${data.token}/ai-analysis`).catch(() => {});
          return;
        }
        if (data.status === "expired" || n >= 15) return;
      } catch { /* retry */ }
      setTries(n + 1);
      setTimeout(() => alive && poll(n + 1), 2000);
    };
    poll(0);
    return () => { alive = false; };
  }, [sid, cancelled, urlToken]);

  const token = st?.token || sp.get("token") || localStorage.getItem(TOKEN_KEY);

  if (cancelled) return (
    <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="payment-cancelled">
      <XCircle className="mx-auto text-slate-400" size={40} />
      <h1 className="text-3xl font-bold mt-4">No worries.</h1>
      <p className="text-slate-600 mt-3">Your results are safe. You can still download the static report for $0.</p>
      {token && <Link to={`/results/${token}`} data-testid="back-to-results-btn" className="mt-6 inline-block rounded-full bg-[#1C242B] text-white px-6 py-3 text-sm font-semibold">Back to my results</Link>}
    </div>
  );

  const paid = st?.payment_status === "paid";
  return (
    <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="payment-success-page">
      {!paid ? (
        <>
          <Loader2 className="mx-auto animate-spin text-[#D96B43]" size={40} />
          <h1 className="text-3xl font-bold mt-4" data-testid="payment-status-checking">Confirming your contribution…</h1>
          {tries >= 15 && <p className="text-slate-600 mt-3">This is taking longer than usual. Refresh in a moment.</p>}
        </>
      ) : (
        <>
          <CheckCircle2 className="mx-auto text-[#3E6B5C]" size={44} />
          <h1 className="text-3xl font-bold mt-4" data-testid="payment-confirmed">Thank you, truly.</h1>
          <p className="text-slate-600 mt-3">Your contribution helps keep this tool free for the next person looking for a change.</p>
          {st.report_type === "static" ? (
            <a href={pdfUrl(st.token, "static")} data-testid="paid-download-static-btn" className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#D96B43] text-white px-6 py-3 text-sm font-semibold hover:bg-[#C05630] transition-colors">
              <Download size={16} /> Download your PDF
            </a>
          ) : (
            <Link to={`/results/${st.token}/report`} data-testid="paid-open-ai-report-btn" className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#D96B43] text-white px-6 py-3 text-sm font-semibold hover:bg-[#C05630] transition-colors">
              Read your AI analysis &amp; download <ArrowUpRight size={16} />
            </Link>
          )}
          <div className="mt-4"><Link to={`/results/${st.token}`} className="text-sm underline text-slate-600">Back to results</Link></div>
        </>
      )}
    </div>
  );
}
